import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import SearchForm from "../../components/SearchForm";
import { URL, getToken, getLocalDate } from "../../utils";
import axios from "axios";
import ConditionalRender from "../../components/ConditionalRender";
import FilterModal from "../../components/FilterModal";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Prospects() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [total, setTotal] = useState(0);
  const token = getToken();

  // Mémoïsation de la fonction de récupération des prospects
  const fetchProspects = useCallback(
    async (lastId = null) => {
      try {
        if (lastId == null) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        const params = lastId != null ? `?offset=${lastId}` : "";
        const response = await axios.get(
          `${URL}Prospects/listes-prospects${params}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const newProspects = response.data?.data ?? [];
        setProspects((prev) =>
          lastId == null ? newProspects : [...prev, ...newProspects],
        );
        setHasMore(response.data?.has_more ?? false);
        setNextOffset(response.data?.next_offset ?? null);
        setTotal(response.data?.total ?? 0);
      } catch (error) {
        console.error("Erreur lors du chargement des prospects:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  // Chargement de la page suivante lorsqu'on atteint le bas de la liste
  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && nextOffset != null) {
      fetchProspects(nextOffset);
    }
  }, [hasMore, loadingMore, nextOffset, fetchProspects]);

  const sentinelRef = useInfiniteScroll({
    hasMore: hasMore && !isFiltered && !searchTerm,
    loading: loading || loadingMore,
    onLoadMore: loadMore,
  });

  // Optimisation du filtrage avec useMemo
  const displayedProspects = useMemo(() => {
    const prospectsToFilter = isFiltered ? filteredProspects : prospects;
    if (!searchTerm) return prospectsToFilter;
    return prospectsToFilter.filter((prospect) =>
      prospect.nom.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [prospects, filteredProspects, searchTerm, isFiltered]);

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleApplyFilters = (filteredData) => {
    setFilteredProspects(filteredData);
    setIsFiltered(true);
  };

  const handleClearFilters = () => {
    setIsFiltered(false);
    setFilteredProspects([]);
  };

  return (
    <>
      <ToastContainer />
      <div className="shadow-md bg-white p-4 flex justify-between items-center sticky top-16 z-10">
        <SearchForm
          onSearch={handleSearch}
          placeholder="Rechercher un prospect"
        />

        <div className="flex items-center gap-2">
          {/* Bouton Filtres */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="p-2 px-4 rounded-md bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 border-2 border-blue-400 focus:ring-offset-2 focus:ring-blue-400 text-sm max-sm:hidden"
          >
            Filtres
          </button>

          {/* Bouton mobile pour filtres */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="text-gray-600 hover:text-blue-500 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-blue-100 focus:outline-none rounded-md p-1 sm:hidden"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
              />
            </svg>
          </button>

          {/* Bouton pour effacer les filtres si des filtres sont appliqués */}
          {isFiltered && (
            <button
              onClick={handleClearFilters}
              className="p-2 px-4 rounded-md bg-red-500 text-white hover:bg-red-600 focus:ring-2 border-2 border-red-400 focus:ring-offset-2 focus:ring-red-400 text-sm max-sm:hidden"
            >
              Effacer filtres
            </button>
          )}

          <ConditionalRender requiredPermissions="canCreateProspect">
            <Link
              to="/prospects/add"
              className="p-2 px-4 rounded-md bg-amber-500 text-white hover:bg-amber-600 focus:ring-2 border-2 border-amber-400 focus:ring-offset-2 focus:ring-amber-400 text-sm max-sm:hidden"
              aria-label="Ajouter un prospect"
            >
              Ajouter un prospect
            </Link>

            <Link
              to="/prospects/add"
              className="text-gray-600 max-sm:ml-10 hover:text-amber-500 focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-amber-100 focus:outline-none rounded-md p-1 sm:hidden"
              aria-label="Ajouter un prospect (mobile)"
            >
              <AddIcon />
            </Link>
          </ConditionalRender>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="p-4">
          <div className="text-2xl font-semibold flex justify-between items-center p-2">
            <h2>
              Prospects ({displayedProspects.length}
              {!isFiltered && !searchTerm && total > 0 ? ` / ${total}` : ""})
              {isFiltered && (
                <span className="text-sm text-blue-600 font-normal ml-2">
                  (Filtré
                  {filteredProspects.length !== prospects.length
                    ? ` - ${prospects.length} total`
                    : ""}
                  )
                </span>
              )}
            </h2>
            <div className="flex items-center gap-4">
              <PaginationButton direction="prev" />
              <PaginationButton direction="next" />
            </div>
          </div>
          <div className="bg-white grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-4 overflow-auto">
            {displayedProspects.length > 0 ? (
              displayedProspects.map((prospect) => (
                <CardSimpleProspect
                  key={prospect.id}
                  prospect={prospect}
                  to="/prospects/details"
                />
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                {searchTerm
                  ? "Aucun prospect trouvé"
                  : isFiltered
                    ? "Aucun prospect trouvé avec ces filtres"
                    : "Aucun prospect disponible"}
              </div>
            )}
          </div>

          {/* Sentinelle de défilement infini */}
          {!isFiltered && !searchTerm && hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-6">
              {loadingMore && (
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal de filtres */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        filterType="prospects"
        title="Filtrer les prospects"
      />
    </>
  );
}

// Composants séparés pour une meilleure lisibilité et réutilisabilité
const AddIcon = () => (
  <svg
    className="w-8 h-8"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      d="M4.857 3A1.857 1.857 0 0 0 3 4.857v4.286C3 10.169 3.831 11 4.857 11h4.286A1.857 1.857 0 0 0 11 9.143V4.857A1.857 1.857 0 0 0 9.143 3H4.857Zm10 0A1.857 1.857 0 0 0 13 4.857v4.286c0 1.026.831 1.857 1.857 1.857h4.286A1.857 1.857 0 0 0 21 9.143V4.857A1.857 1.857 0 0 0 19.143 3h-4.286Zm-10 10A1.857 1.857 0 0 0 3 14.857v4.286C3 20.169 3.831 21 4.857 21h4.286A1.857 1.857 0 0 0 11 19.143v-4.286A1.857 1.857 0 0 0 9.143 13H4.857ZM18 14a1 1 0 1 0-2 0v2h-2a1 1 0 1 0 0 2h2v2a1 1 0 1 0 2 0v-2h2a1 1 0 1 0 0-2h-2v-2Z"
      clipRule="evenodd"
    />
  </svg>
);

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
  </div>
);

const PaginationButton = ({ direction }) => (
  <Link
    to="#"
    className="flex items-center justify-center p-2 bg-white shadow-md rounded-md"
    aria-label={direction === "prev" ? "Page précédente" : "Page suivante"}
  >
    <svg
      className="w-6 h-6"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d={direction === "prev" ? "m15 19-7-7 7-7" : "m9 5 7 7-7 7"}
      />
    </svg>
  </Link>
);

// Composant mémoïsé pour les cartes de prospect
const CardSimpleProspect = React.memo(({ prospect, to }) => {
  return (
    <Link
      to={`${to}/${prospect.id}`}
      className="flex flex-col gap-3 border border-gray-200 p-4 bg-white shadow-md rounded-md hover:shadow-lg transition-shadow min-w-0 overflow-hidden"
      aria-label={`Détails du prospect ${prospect.nom}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-semibold text-lg">{prospect.nom}</h3>
          <p className="text-sm text-gray-600">
            {prospect.poste ??
              (prospect.secteur_activite === "night_etablissement" ||
              prospect.secteur_activite === "night_etablishement"
                ? "Établissement de nuit"
                : prospect.secteur_activite === "shop"
                  ? "Boutique"
                  : prospect.secteur_activite || "Secteur non spécifié")}
          </p>
        </div>
        <QualifiedBadge qualified={prospect.qualifie} />
      </div>

      <div className="mt-2 space-y-2">
        <PhoneNumber phone={prospect.telephone} />
        <CreationDate date={prospect.date_creation} />
      </div>

      <div className="mt-4 flex justify-between items-center text-sm">
        <CommercialName name={prospect.commercial_name} />
        {prospect.rendez_vous === "Oui" && prospect.date_rendez_vous && (
          <AppointmentDate date={prospect.date_rendez_vous} />
        )}
      </div>
    </Link>
  );
});

// Sous-composants pour la carte prospect
const QualifiedBadge = ({ qualified }) => (
  <span
    className={`px-2 py-1 rounded-full text-xs ${
      qualified === "Oui"
        ? "bg-green-100 text-green-800"
        : "bg-amber-100 text-amber-800"
    }`}
  >
    {qualified}
  </span>
);

const PhoneNumber = ({ phone }) => (
  <p className="text-amber-600 font-medium flex items-center gap-2">
    <PhoneIcon />
    {phone}
  </p>
);

const PhoneIcon = () => (
  <svg
    className="w-5 h-5"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M18.427 14.768 17.2 13.542a1.733 1.733 0 0 0-2.45 0l-.613.613a1.732 1.732 0 0 1-2.45 0l-1.838-1.84a1.735 1.735 0 0 1 0-2.452l.612-.613a1.735 1.735 0 0 0 0-2.452L9.237 5.572a1.6 1.6 0 0 0-2.45 0c-3.223 3.2-1.702 6.896 1.519 10.117 3.22 3.221 6.914 4.745 10.12 1.535a1.601 1.601 0 0 0 0-2.456Z"
    />
  </svg>
);

const CreationDate = ({ date }) => (
  <p className="text-gray-500 font-medium flex items-center gap-2">
    <CalendarIcon />
    Créé le: {getLocalDate(new Date(date))}
  </p>
);

const CalendarIcon = () => (
  <svg
    className="w-5 h-5 inline-block mr-1 text-gray-500"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
    />
  </svg>
);

const CommercialName = ({ name }) => (
  <p className="text-gray-500">Commercial: {name || "Non attribué"}</p>
);

const AppointmentDate = ({ date }) => (
  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
    RDV: {getLocalDate(new Date(date))}
  </span>
);
